import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import {
  AUTH_DEFAULT_COUNTRY,
  AUTH_ERROR_CODES,
  AUTH_ERROR_MESSAGES,
  AUTH_UA_CALLING_CODE,
} from '../authRegistrationConstants'

export const UA_NATIONAL_LENGTH = 9
export const UA_PHONE_PLACEHOLDER = '00 000 00 00'

/**
 * Strip any UA phone paste/input down to at most 9 national digits (after 380 / leading 0).
 * @param {string} raw
 * @returns {string}
 */
export function extractUaNationalDigits(raw) {
  let digits = String(raw || '').replace(/\D/g, '')

  // Autofill may paste +380… (12 digits). Never strip when already ≤9 national digits.
  if (digits.startsWith(AUTH_UA_CALLING_CODE) && digits.length > UA_NATIONAL_LENGTH) {
    digits = digits.slice(AUTH_UA_CALLING_CODE.length)
  }

  // Trunk 0XXXXXXXXX (10 digits) or 0 + partial
  if (digits.startsWith('0')) {
    digits = digits.slice(1)
  }

  return digits.slice(0, UA_NATIONAL_LENGTH)
}

/**
 * Progressive national display: 68 899 97 99
 * Spaces only — easier to scan while typing, fewer caret fights than (XX) XXX-XX-XX.
 * @param {string} nationalDigits
 * @returns {string}
 */
export function formatUaNationalDisplay(nationalDigits) {
  const d = extractUaNationalDigits(nationalDigits)
  const len = d.length
  if (len === 0) return ''
  if (len <= 2) return d
  if (len <= 5) return `${d.slice(0, 2)} ${d.slice(2)}`
  if (len <= 7) return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5)}`
  return `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 7)} ${d.slice(7, 9)}`
}

/**
 * @param {string} nationalDigits
 * @returns {{
 *   nationalDigits: string,
 *   display: string,
 *   e164: string | null,
 *   isComplete: boolean,
 *   isValid: boolean,
 * }}
 */
export function buildUaPhoneValue(nationalDigits) {
  const digits = extractUaNationalDigits(nationalDigits)
  const display = formatUaNationalDisplay(digits)
  const isComplete = digits.length === UA_NATIONAL_LENGTH

  if (!isComplete) {
    return {
      nationalDigits: digits,
      display,
      e164: null,
      isComplete: false,
      isValid: false,
    }
  }

  const e164 = `+${AUTH_UA_CALLING_CODE}${digits}`
  const parsed = parsePhoneNumberFromString(e164, AUTH_DEFAULT_COUNTRY)
  const isValid = Boolean(
    parsed &&
      parsed.isValid() &&
      String(parsed.countryCallingCode) === String(AUTH_UA_CALLING_CODE) &&
      (parsed.country === AUTH_DEFAULT_COUNTRY || !parsed.country)
  )

  return {
    nationalDigits: digits,
    display,
    e164: isValid ? parsed.format('E.164') : e164,
    isComplete: true,
    isValid,
  }
}

/**
 * Validate for submit / adapter. Accepts national digits, display, or full E.164-ish raw.
 * @param {string} rawOrNational
 */
export function normalizeUaPhoneToE164(rawOrNational) {
  const value = buildUaPhoneValue(rawOrNational)

  if (!value.nationalDigits) {
    return {
      ok: false,
      errorCode: AUTH_ERROR_CODES.INVALID_PHONE,
      message: AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.INVALID_PHONE],
    }
  }

  if (!value.isComplete) {
    return {
      ok: false,
      errorCode: AUTH_ERROR_CODES.INVALID_PHONE,
      message: AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.INVALID_PHONE],
    }
  }

  if (!value.isValid) {
    return {
      ok: false,
      errorCode: AUTH_ERROR_CODES.INVALID_PHONE,
      message: AUTH_ERROR_MESSAGES[AUTH_ERROR_CODES.INVALID_PHONE],
    }
  }

  return {
    ok: true,
    e164: value.e164,
    nationalNumber: value.nationalDigits,
  }
}

export function countDigitsBeforeIndex(str, index) {
  let n = 0
  const limit = Math.max(0, Math.min(index, String(str || '').length))
  for (let i = 0; i < limit; i += 1) {
    if (/\d/.test(str[i])) n += 1
  }
  return n
}

export function caretIndexForDigitCount(formatted, digitCount) {
  if (digitCount <= 0) return 0
  let seen = 0
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      seen += 1
      if (seen === digitCount) return i + 1
    }
  }
  return formatted.length
}

/**
 * Mask E.164 for OTP copy: +380501111111 → +380 50 ••• •• 11
 * @param {string} e164
 * @returns {string}
 */
export function maskPhoneE164(e164) {
  const digits = String(e164 || '').replace(/\D/g, '')
  if (digits.length < 12) {
    return e164 || ''
  }
  const cc = digits.slice(0, 3)
  const op = digits.slice(3, 5)
  const last2 = digits.slice(-2)
  return `+${cc} ${op} ••• •• ${last2}`
}
